import { validatedDisplayView } from "../../i18n/display-view.js";

/**
 * Area `subscription` of the console gateway — over `apps/api/src/routes/subscription/` (design §27.9.2). Owned by plan-014
 * unit **E2 (read by E4)**; filled by plan-014 **E4** with what Finance → Plans reads and quotes (design §27.15.1;
 * FR12-CAT-04/05/07, FR12-ENT-08; AC12-08):
 *
 *   readBalanceSubscription(billingAccountId)       `GET /v1/billing-accounts/{id}/subscription` — the balance's running
 *                                                   subscription, or `null` (pay-as-you-go — every balance at launch)
 *   quotePlanPurchase(accountId, request)           `POST /v1/accounts/{id}/billing-quotes` with `subscription_purchase`
 *                                                   — a FROZEN quote (lines, VAT, due today, expiry); nothing is charged
 *   listResourceRestrictions(accountId)             `GET /v1/accounts/{id}/resource-restrictions` — E4's disclosure of
 *                                                   what a lowered limit left paused, read-only or closed to new ones
 *   readEntitlementImpact(billingAccountId, revId)  `GET /v1/billing-accounts/{id}/entitlement-impact` — the over-limit
 *                                                   preview a downgrade shows before it is confirmed
 *
 * AMENDED 2026-10-17 (plan-020 **WAPP-PLAN**; design §32.6.1, §32.6.2, §32.6.7, §32.17):
 *
 *   readBalanceSubscription(billingAccountId)       now also carries what the plan screens show (§32.6.7): the plan's
 *                                                   name, the period, the included seconds granted/used/left, the extra
 *                                                   seconds and their cost as of the last day close (`extra_as_of`), the
 *                                                   extra-time switch, the next charge with its VAT, the card and its
 *                                                   mandate state, the collection mode, the grace end, the renewal waiting
 *                                                   for the person (`pay_operation_id`) — WAPP-BILL's Plan section reads
 *                                                   the same view
 *   quoteSubscription(accountId, request)           `POST /v1/accounts/{id}/billing-quotes` with a purchase, a change or
 *                                                   a cancellation — a FROZEN quote with its plan detail (§32.6.2: the
 *                                                   first and the recurring charge, the anchor day, the included seconds,
 *                                                   the rates by kind with their source, calls at once before and after,
 *                                                   the extra-time state, the covered workspaces, the agents above a new
 *                                                   limit, whether a consent is asked and to which words)
 *   withdrawSubscriptionCancellation(id)            `POST /v1/billing-subscriptions/{id}/withdraw-cancellation`
 *
 * A refusal keeps the API's `error.detail.reason` (`subscription_tax_id_required`: `missing` | `invalid` | `unchecked` |
 * `try_later`) on {@link SubscriptionRefusalError}, so the screen can say which remedy applies.
 *
 * The two E4 operations are in the `entitlement` area's fragments; the api-client lock holder regenerates the client.
 * Until then each is reached through {@link documentedOperation} — the operationId must be in the assembled spec AND a
 * method of the generated client, or the call raises {@link UndocumentedOperationError} naming it (the OP-36
 * precedent, `./customer-order-payment.ts`): no path string, no escape hatch. Once regenerated they resolve with no edit.
 */
import { API_OPERATION, ApiError, assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";
import { CURRENCY_CODE, money, type Money } from "@wetalk/domain";

import { resolvedTalkRateOfWire, type OfferTextView, type ResolvedTalkRateView } from "./commercial-offer.js";

import type { ConsoleGateway } from "../gateway.js";
import { readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import { UndocumentedOperationError, type GatewayGap, type SliceContext } from "./slice-context.js";

// ── views ─────────────────────────────────────────────────────────────────────────────────────────────────

/** The card a running subscription renews by (four digits only) and its mandate's state. */
export interface SubscriptionCardView {
  readonly lastFour: string | null;
  readonly mandateState: "valid" | "revoked" | "none" | "unknown";
}

/** The next renewal's charge, VAT at the payer's treatment in force now. */
export interface SubscriptionNextChargeView {
  readonly net: Money;
  readonly vat: Money;
  readonly total: Money;
  readonly at: string;
}

/** A balance's running subscription, as the Plans page names it. */
export interface BalanceSubscriptionView {
  readonly billingSubscriptionId: string;
  readonly state: string;
  readonly commercialRevisionId: string;
  readonly offerCode: string | null;
  readonly recurrence: string | null;
  readonly currentPeriodEnd: string | null;
  readonly cancelAtPeriodEnd: boolean;
  readonly scheduledChange: readonly { readonly kind: string; readonly effectiveAt: string; readonly targetCommercialRevisionId: string | null }[];
  // ── plan-020 (design §32.6.7) ──
  /** The offer's name as its presentation names it; `null` for a custom offer. */
  readonly displayName: OfferTextView | null;
  readonly billingPeriod: "monthly" | "annual";
  /** The current period `[start, end)`; `null` while pending activation. */
  readonly period: { readonly start: string; readonly end: string } | null;
  readonly renewalAnchorDay: number;
  readonly billingTimezone: string;
  readonly includedTalkSecondGranted: number;
  readonly includedTalkSecondUsed: number;
  readonly includedTalkSecondLeft: number;
  /** Paid seconds beyond the included ones this period, as of the last day close. */
  readonly extraTalkSecond: number;
  readonly extraAmount: Money;
  readonly extraAsOf: "last_day_close";
  readonly extraTimeEnabled: boolean;
  /** `null` while a cancellation is scheduled or the subscription is not running. */
  readonly nextCharge: SubscriptionNextChargeView | null;
  readonly card: SubscriptionCardView | null;
  readonly collectionMode: "mandate" | "hosted_notification";
  readonly graceEndsAt: string | null;
  /** The renewal operation waiting for the person ("Pay now"). */
  readonly payOperationId: string | null;
}

/** How VAT applies to a plan fee — never a consumer's destination VAT (OA-4). */
export type SubscriptionTaxTreatmentView = "domestic_greece" | "reverse_charge" | "outside_scope";

/** A plan's charge: net + VAT = total. */
export interface SubscriptionChargeView {
  readonly net: Money;
  readonly vat: Money;
  readonly total: Money;
}

/**
 * What a subscription quote shows before the pay button (design §32.6.2, block 4.1 of the card-rules note) — the
 * `detail` SUB-KIND freezes, decoded from its snake_case wire twin.
 */
export interface SubscriptionQuoteDetailView {
  /** The offer the quote moves to; `null` when it moves back to pay as you go (a cancellation). */
  readonly offer: { readonly offerCode: string; readonly commercialRevisionId: string; readonly displayName: OfferTextView | null } | null;
  readonly billingPeriod: "monthly" | "annual" | null;
  /** What is charged now; `null` when nothing is due now (a downgrade, a cancellation). */
  readonly firstCharge: (SubscriptionChargeView & { readonly taxTreatment: SubscriptionTaxTreatmentView; readonly vatRatePermille: number }) | null;
  /** What each renewal will charge; `null` when the target has no fee. */
  readonly recurringCharge: SubscriptionChargeView | null;
  readonly startAt: string;
  readonly renewalAnchorDay: number | null;
  readonly billingTimezone: string;
  readonly firstRenewalAt: string | null;
  readonly includedTalkSecond: number;
  readonly resolvedTalkRate: readonly ResolvedTalkRateView[];
  readonly channelUnitBefore: number;
  readonly channelUnitAfter: number;
  readonly extraTimeEnabled: boolean;
  readonly coveredWorkspace: readonly { readonly workspaceId: string; readonly name: string }[];
  readonly uncoveredWorkspace: readonly { readonly workspaceId: string; readonly name: string }[];
  readonly overLimitVoiceAgent: readonly { readonly voiceAgentId: string; readonly name: string; readonly channelUnitLimit: number }[];
  readonly consentRequired: boolean;
  readonly consentTextRevision: string | null;
  readonly cardLastFour: string | null;
  /**
   * The days of notice before a change to a running plan's charge (`commercialPolicy.subscriptionTermsNoticeDay`,
   * OD-29), which every plan quote's detail names (`terms_notice_day`, plan-020 hand-off, 2026-10-17). A detail without
   * it is refused — never a figure typed here.
   */
  readonly termsNoticeDay: number;
}

/** A frozen subscription quote: the amounts and the plan detail. */
export interface SubscriptionQuoteView extends PlanQuoteView {
  readonly detail: SubscriptionQuoteDetailView;
}

/** The quotes the plan screens ask for (design §32.6.1). A reactivation is a purchase while no subscription runs. */
export type SubscriptionQuoteRequestView =
  | { readonly kind: "subscription_purchase" | "subscription_change"; readonly billingAccountId: string; readonly commercialRevisionId: string }
  | { readonly kind: "subscription_cancellation"; readonly billingSubscriptionId: string };

/** Why `subscription_tax_id_required` refused (design §32.6.5). */
export type TaxIdRefusalReasonView = "missing" | "invalid" | "unchecked" | "try_later";

/**
 * An API refusal that keeps the envelope's `error.detail` (the generated client's {@link ApiError} drops it): today only
 * `subscription_tax_id_required`'s `reason` is read.
 */
export class SubscriptionRefusalError extends ApiError {
  readonly reason: string | null;
  readonly detail: Readonly<Record<string, unknown>> | null;
  constructor(status: number, body: unknown, requestId: string | null) {
    super(status, body, requestId);
    const envelope = (body as { error?: { detail?: unknown } } | null)?.error;
    const detail = envelope !== undefined && typeof envelope.detail === "object" && envelope.detail !== null && !Array.isArray(envelope.detail) ? (envelope.detail as Readonly<Record<string, unknown>>) : null;
    this.detail = detail;
    this.reason = detail !== null && typeof detail["reason"] === "string" ? detail["reason"] : null;
  }
}

/** {@link assertOk}, raising {@link SubscriptionRefusalError} so a refusal's detail survives. */
export function assertSubscriptionOk(result: ApiResult): unknown {
  if (result.status >= 200 && result.status < 300) return result.body;
  throw new SubscriptionRefusalError(result.status, result.body, result.requestId);
}

/** One frozen quote line (net and VAT after the line's one rounding; negative for a credit). */
export interface PlanQuoteLineView {
  readonly kind: string;
  readonly netMinor: number;
  readonly taxMinor: number;
}

/** A frozen plan quote: what is due, until when it holds. */
export interface PlanQuoteView {
  readonly billingQuoteId: string;
  readonly kind: string;
  readonly line: readonly PlanQuoteLineView[];
  readonly netMinor: number;
  readonly taxMinor: number;
  readonly amountDueMinor: number;
  readonly currency: string;
  readonly taxRatePermille: number | null;
  readonly effectiveAt: string;
  readonly expiresAt: string;
}

export type RestrictedKindView = "voice_agent" | "phone_number" | "seat" | "recording_storage" | "channel_unit" | "workspace";
export type RestrictionView = "no_new" | "paused" | "read_only" | "no_new_storage";
const RESTRICTED_KIND: readonly RestrictedKindView[] = ["voice_agent", "phone_number", "seat", "recording_storage", "channel_unit", "workspace"];
const RESTRICTION: readonly RestrictionView[] = ["no_new", "paused", "read_only", "no_new_storage"];

/** One standing restriction — disclosed, never a deletion. */
export interface ResourceRestrictionView {
  readonly resourceRestrictionId: string;
  readonly resourceKind: RestrictedKindView;
  readonly resourceId: string | null;
  readonly resourceName: string | null;
  readonly restriction: RestrictionView;
  readonly disclosedAt: string;
}

/** The over-limit preview of moving a balance to an offer. */
export interface EntitlementImpactView {
  readonly withinLimit: boolean;
  readonly channelUnit: { readonly from: number; readonly to: number };
  readonly line: readonly {
    readonly resourceKind: RestrictedKindView;
    readonly used: number;
    readonly limitAfter: number;
    readonly overBy: number;
    readonly restriction: RestrictionView;
    readonly affected: readonly { readonly id: string; readonly name: string }[];
  }[];
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** plan-014 E4: the balance's running subscription, or `null` (pay-as-you-go). */
    readBalanceSubscription(billingAccountId: string): Promise<BalanceSubscriptionView | null>;
    /** plan-020 WAPP-PLAN: a frozen subscription quote with its plan detail — nothing is charged. */
    quoteSubscription(accountId: string, request: SubscriptionQuoteRequestView): Promise<SubscriptionQuoteView>;
    /** plan-020 WAPP-PLAN: keeps the plan — the cancellation scheduled for the end of the period is withdrawn. */
    withdrawSubscriptionCancellation(billingSubscriptionId: string): Promise<BalanceSubscriptionView>;
    /** plan-014 E4: the restrictions a lowered limit left standing. */
    listResourceRestrictions(accountId: string): Promise<readonly ResourceRestrictionView[]>;
    /** plan-014 E4: what would be over each limit if the balance moved to the offer. */
    readEntitlementImpact(billingAccountId: string, commercialRevisionId: string): Promise<EntitlementImpactView>;
  }
}

/** The methods this slice puts on `ConsoleGateway`. */
export type SubscriptionGatewaySlice = Pick<
  ConsoleGateway,
  "readBalanceSubscription" | "quoteSubscription" | "withdrawSubscriptionCancellation" | "listResourceRestrictions" | "readEntitlementImpact"
>;

/** This area's rows of `GATEWAY_GAP`: none — the E4 operations are refused per call until the spec carries them. */
export const subscriptionGap: readonly GatewayGap[] = [];

/** The two E4 operations, named for {@link UndocumentedOperationError}. */
export const PLAN_OPERATION = validatedDisplayView({
  get restriction() { return validatedDisplayView({ method: "listResourceRestrictions", wants: "listResourceRestrictions", get owner() { return "plan-014 E4"; } }); },
  get impact() { return validatedDisplayView({ method: "readEntitlementImpact", wants: "getEntitlementImpact", get owner() { return "plan-014 E4"; } }); },
}) satisfies Readonly<Record<string, GatewayGap>>;

type DocumentedOperation = (request: {
  readonly path: Readonly<Record<string, string>>;
  readonly query?: Readonly<Record<string, string>>;
}) => Promise<ApiResult>;

/** The generated client's method for a DOCUMENTED operation, or {@link UndocumentedOperationError} (synchronous). */
export function documentedOperation(client: WeTalkApiClient, gap: GatewayGap): DocumentedOperation {
  const documented = API_OPERATION.some((one) => one.operationId === gap.wants);
  const method: unknown = Reflect.get(client, gap.wants);
  if (!documented || typeof method !== "function") throw new UndocumentedOperationError(gap);
  return (request) => Promise.resolve(Reflect.apply(method, client, [request]) as Promise<ApiResult>);
}

// ── decoding ──────────────────────────────────────────────────────────────────────────────────────────────

function textOrNull(value: unknown, path: string): string | null {
  return value === null || value === undefined ? null : readText(value, path);
}

function signed(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) throw new Error(`${path} must be a whole number`);
  return value;
}

function dataOf(result: ApiResult, operationId: string): Readonly<Record<string, unknown>> {
  return readRecord(readRecord(assertOk(result), operationId)["data"], `${operationId}.data`);
}

function minorOf(value: unknown, path: string): Money {
  return money(readWholeNumber(value, path), CURRENCY_CODE);
}

function localisedOrNull(value: unknown, path: string): OfferTextView | null {
  if (value === null || value === undefined) return null;
  const text = readRecord(value, path);
  return { en: readText(text["en"], `${path}.en`), el: readText(text["el"], `${path}.el`) };
}

function oneOfText<const T extends string>(allowed: readonly T[], value: unknown, path: string): T {
  const found = allowed.find((one) => one === value);
  if (found === undefined) throw new Error(`${path}: ${JSON.stringify(value)} is not a value this console knows`);
  return found;
}

const MANDATE_STATE = ["valid", "revoked", "none", "unknown"] as const;
const COLLECTION_MODE = ["mandate", "hosted_notification"] as const;
const BILLING_PERIOD = ["monthly", "annual"] as const;
const TAX_TREATMENT = ["domestic_greece", "reverse_charge", "outside_scope"] as const;

/** One running subscription (`BillingSubscription`). Exported for the sandbox half and the suites. */
export function subscriptionOfWire(one: Readonly<Record<string, unknown>>, path: string): BalanceSubscriptionView {
  const revision = readRecord(one["commercial_revision"], `${path}.commercial_revision`);
  const period = one["current_period"];
  const offer = readRecord(one["offer"], `${path}.offer`);
  const shownPeriod = one["period"];
  const next = one["next_charge"];
  const card = one["card"];
  return {
    billingSubscriptionId: readText(one["billing_subscription_id"], `${path}.billing_subscription_id`),
    state: readText(one["state"], `${path}.state`),
    commercialRevisionId: readText(revision["commercial_revision_id"], `${path}.commercial_revision.commercial_revision_id`),
    offerCode: textOrNull(revision["offer_code"], `${path}.commercial_revision.offer_code`),
    recurrence: textOrNull(revision["recurrence"], `${path}.commercial_revision.recurrence`),
    currentPeriodEnd: period === null || period === undefined ? null : readText(readRecord(period, `${path}.current_period`)["end"], `${path}.current_period.end`),
    cancelAtPeriodEnd: readBoolean(one["cancel_at_period_end"], `${path}.cancel_at_period_end`),
    scheduledChange: readArray(one["scheduled_change"], `${path}.scheduled_change`).map((change, index) => {
      const each = readRecord(change, `${path}.scheduled_change[${index}]`);
      return {
        kind: readText(each["kind"], `${path}.scheduled_change.kind`),
        effectiveAt: readText(each["effective_at"], `${path}.scheduled_change.effective_at`),
        targetCommercialRevisionId: textOrNull(each["target_commercial_revision_id"], `${path}.scheduled_change.target_commercial_revision_id`),
      };
    }),
    displayName: localisedOrNull(offer["display_name"], `${path}.offer.display_name`),
    billingPeriod: oneOfText(BILLING_PERIOD, one["billing_period"], `${path}.billing_period`),
    period:
      shownPeriod === null || shownPeriod === undefined
        ? null
        : (() => {
            const range = readRecord(shownPeriod, `${path}.period`);
            return { start: readText(range["start"], `${path}.period.start`), end: readText(range["end"], `${path}.period.end`) };
          })(),
    renewalAnchorDay: readWholeNumber(one["renewal_anchor_day"], `${path}.renewal_anchor_day`),
    billingTimezone: readText(one["billing_timezone"], `${path}.billing_timezone`),
    includedTalkSecondGranted: readWholeNumber(one["included_talk_second_granted"], `${path}.included_talk_second_granted`),
    includedTalkSecondUsed: readWholeNumber(one["included_talk_second_used"], `${path}.included_talk_second_used`),
    includedTalkSecondLeft: readWholeNumber(one["included_talk_second_left"], `${path}.included_talk_second_left`),
    extraTalkSecond: readWholeNumber(one["extra_talk_second"], `${path}.extra_talk_second`),
    extraAmount: minorOf(one["extra_amount_minor"], `${path}.extra_amount_minor`),
    extraAsOf: oneOfText(["last_day_close"] as const, one["extra_as_of"], `${path}.extra_as_of`),
    extraTimeEnabled: readBoolean(one["extra_time_enabled"], `${path}.extra_time_enabled`),
    nextCharge:
      next === null || next === undefined
        ? null
        : (() => {
            const charge = readRecord(next, `${path}.next_charge`);
            return {
              net: minorOf(charge["net_minor"], `${path}.next_charge.net_minor`),
              vat: minorOf(charge["vat_minor"], `${path}.next_charge.vat_minor`),
              total: minorOf(charge["total_minor"], `${path}.next_charge.total_minor`),
              at: readText(charge["at"], `${path}.next_charge.at`),
            };
          })(),
    card:
      card === null || card === undefined
        ? null
        : (() => {
            const held = readRecord(card, `${path}.card`);
            return { lastFour: textOrNull(held["last_four"], `${path}.card.last_four`), mandateState: oneOfText(MANDATE_STATE, held["mandate_state"], `${path}.card.mandate_state`) };
          })(),
    collectionMode: oneOfText(COLLECTION_MODE, one["collection_mode"], `${path}.collection_mode`),
    graceEndsAt: textOrNull(one["grace_ends_at"], `${path}.grace_ends_at`),
    payOperationId: textOrNull(one["pay_operation_id"], `${path}.pay_operation_id`),
  };
}

/** `{ billing_account_id, subscription }` → the running subscription or `null`. Exported for the suites. */
export function balanceSubscriptionOfWire(data: Readonly<Record<string, unknown>>, path: string): BalanceSubscriptionView | null {
  const raw = data["subscription"];
  if (raw === null || raw === undefined) return null;
  return subscriptionOfWire(readRecord(raw, `${path}.subscription`), `${path}.subscription`);
}

function chargeOfWire(value: unknown, path: string): SubscriptionChargeView {
  const charge = readRecord(value, path);
  const net = minorOf(charge["net_minor"], `${path}.net_minor`);
  const vat = minorOf(charge["vat_minor"], `${path}.vat_minor`);
  const total = minorOf(charge["total_minor"], `${path}.total_minor`);
  if (net.amountMinor + vat.amountMinor !== total.amountMinor) throw new Error(`${path}: net + VAT is not the total`);
  return { net, vat, total };
}

function namedWorkspacesOf(value: unknown, path: string): readonly { readonly workspaceId: string; readonly name: string }[] {
  return readArray(value, path).map((raw, index) => {
    const one = readRecord(raw, `${path}[${String(index)}]`);
    return { workspaceId: readText(one["workspace_id"], `${path}.workspace_id`), name: readText(one["name"], `${path}.name`) };
  });
}

/** A subscription quote's plan detail (design §32.6.2's wire twin). Exported for the suites. */
export function subscriptionQuoteDetailOfWire(detail: Readonly<Record<string, unknown>>, path: string): SubscriptionQuoteDetailView {
  const offerRaw = detail["offer"];
  const first = detail["first_charge"];
  const recurring = detail["recurring_charge"];
  const period = detail["billing_period"];
  return {
    offer:
      offerRaw === null || offerRaw === undefined
        ? null
        : (() => {
            const offer = readRecord(offerRaw, `${path}.offer`);
            return {
              offerCode: readText(offer["offer_code"], `${path}.offer.offer_code`),
              commercialRevisionId: readText(offer["commercial_revision_id"], `${path}.offer.commercial_revision_id`),
              displayName: localisedOrNull(offer["display_name"], `${path}.offer.display_name`),
            };
          })(),
    billingPeriod: period === null || period === undefined ? null : oneOfText(BILLING_PERIOD, period, `${path}.billing_period`),
    firstCharge:
      first === null || first === undefined
        ? null
        : (() => {
            const charge = readRecord(first, `${path}.first_charge`);
            return {
              ...chargeOfWire(charge, `${path}.first_charge`),
              taxTreatment: oneOfText(TAX_TREATMENT, charge["tax_treatment"], `${path}.first_charge.tax_treatment`),
              vatRatePermille: readWholeNumber(charge["vat_rate_permille"], `${path}.first_charge.vat_rate_permille`),
            };
          })(),
    recurringCharge: recurring === null || recurring === undefined ? null : chargeOfWire(recurring, `${path}.recurring_charge`),
    startAt: readText(detail["start_at"], `${path}.start_at`),
    renewalAnchorDay: detail["renewal_anchor_day"] === null || detail["renewal_anchor_day"] === undefined ? null : readWholeNumber(detail["renewal_anchor_day"], `${path}.renewal_anchor_day`),
    billingTimezone: readText(detail["billing_timezone"], `${path}.billing_timezone`),
    firstRenewalAt: textOrNull(detail["first_renewal_at"], `${path}.first_renewal_at`),
    includedTalkSecond: readWholeNumber(detail["included_talk_second"], `${path}.included_talk_second`),
    resolvedTalkRate: readArray(detail["resolved_talk_rate"], `${path}.resolved_talk_rate`).map((one, index) => resolvedTalkRateOfWire(one, `${path}.resolved_talk_rate[${String(index)}]`)),
    channelUnitBefore: readWholeNumber(detail["channel_unit_before"], `${path}.channel_unit_before`),
    channelUnitAfter: readWholeNumber(detail["channel_unit_after"], `${path}.channel_unit_after`),
    extraTimeEnabled: readBoolean(detail["extra_time_enabled"], `${path}.extra_time_enabled`),
    coveredWorkspace: namedWorkspacesOf(detail["covered_workspace"], `${path}.covered_workspace`),
    uncoveredWorkspace: namedWorkspacesOf(detail["uncovered_workspace"], `${path}.uncovered_workspace`),
    overLimitVoiceAgent: readArray(detail["over_limit_voice_agent"], `${path}.over_limit_voice_agent`).map((raw, index) => {
      const one = readRecord(raw, `${path}.over_limit_voice_agent[${String(index)}]`);
      return {
        voiceAgentId: readText(one["voice_agent_id"], `${path}.over_limit_voice_agent.voice_agent_id`),
        name: readText(one["name"], `${path}.over_limit_voice_agent.name`),
        channelUnitLimit: readWholeNumber(one["channel_unit_limit"], `${path}.over_limit_voice_agent.channel_unit_limit`),
      };
    }),
    consentRequired: readBoolean(detail["consent_required"], `${path}.consent_required`),
    consentTextRevision: textOrNull(detail["consent_text_revision"], `${path}.consent_text_revision`),
    cardLastFour: textOrNull(detail["card_last_four"], `${path}.card_last_four`),
    termsNoticeDay: readWholeNumber(detail["terms_notice_day"], `${path}.terms_notice_day`),
  };
}

/** A frozen subscription quote with its plan detail. Exported for the suites. */
export function subscriptionQuoteOfWire(data: Readonly<Record<string, unknown>>, path: string): SubscriptionQuoteView {
  return { ...planQuoteOfWire(data, path), detail: subscriptionQuoteDetailOfWire(readRecord(data["detail"], `${path}.detail`), `${path}.detail`) };
}

/** The request on the wire (design §27.9.3's union, the subscription members). */
export function subscriptionQuoteRequestBodyOf(request: SubscriptionQuoteRequestView): Readonly<Record<string, unknown>> {
  return request.kind === "subscription_cancellation"
    ? { kind: request.kind, billing_subscription_id: request.billingSubscriptionId }
    : { kind: request.kind, billing_account_id: request.billingAccountId, commercial_revision_id: request.commercialRevisionId };
}

/** A frozen quote (`BillingQuote`). Exported for the suites. */
export function planQuoteOfWire(data: Readonly<Record<string, unknown>>, path: string): PlanQuoteView {
  const rate = data["tax_rate_permille"];
  return {
    billingQuoteId: readText(data["billing_quote_id"], `${path}.billing_quote_id`),
    kind: readText(data["kind"], `${path}.kind`),
    line: readArray(data["line"], `${path}.line`).map((raw, index) => {
      const one = readRecord(raw, `${path}.line[${index}]`);
      return { kind: readText(one["kind"], `${path}.line.kind`), netMinor: signed(one["net_minor"], `${path}.line.net_minor`), taxMinor: signed(one["tax_minor"], `${path}.line.tax_minor`) };
    }),
    netMinor: signed(data["net_minor"], `${path}.net_minor`),
    taxMinor: signed(data["tax_minor"], `${path}.tax_minor`),
    amountDueMinor: readWholeNumber(data["amount_due_minor"], `${path}.amount_due_minor`),
    currency: readText(data["currency"], `${path}.currency`),
    taxRatePermille: rate === null || rate === undefined ? null : readWholeNumber(rate, `${path}.tax_rate_permille`),
    effectiveAt: readText(data["effective_at"], `${path}.effective_at`),
    expiresAt: readText(data["expires_at"], `${path}.expires_at`),
  };
}

function kindOf(value: unknown, path: string): RestrictedKindView {
  const found = RESTRICTED_KIND.find((one) => one === value);
  if (found === undefined) throw new Error(`${path} is not a restricted kind this console knows`);
  return found;
}

function restrictionOf(value: unknown, path: string): RestrictionView {
  const found = RESTRICTION.find((one) => one === value);
  if (found === undefined) throw new Error(`${path} is not a restriction this console knows`);
  return found;
}

/** One standing restriction (`ResourceRestriction`). Exported for the sandbox half and the suites. */
export function resourceRestrictionOfWire(data: Readonly<Record<string, unknown>>, path: string): ResourceRestrictionView {
  return {
    resourceRestrictionId: readText(data["resource_restriction_id"], `${path}.resource_restriction_id`),
    resourceKind: kindOf(data["resource_kind"], `${path}.resource_kind`),
    resourceId: textOrNull(data["resource_id"], `${path}.resource_id`),
    resourceName: textOrNull(data["resource_name"], `${path}.resource_name`),
    restriction: restrictionOf(data["restriction"], `${path}.restriction`),
    disclosedAt: readText(data["disclosed_at"], `${path}.disclosed_at`),
  };
}

/** The preview (`EntitlementImpact`). Exported for the suites. */
export function entitlementImpactOfWire(data: Readonly<Record<string, unknown>>, path: string): EntitlementImpactView {
  const channelUnit = readRecord(data["channel_unit"], `${path}.channel_unit`);
  return {
    withinLimit: readBoolean(data["within_limit"], `${path}.within_limit`),
    channelUnit: { from: readWholeNumber(channelUnit["from"], `${path}.channel_unit.from`), to: readWholeNumber(channelUnit["to"], `${path}.channel_unit.to`) },
    line: readArray(data["line"], `${path}.line`).map((raw, index) => {
      const one = readRecord(raw, `${path}.line[${index}]`);
      return {
        resourceKind: kindOf(one["resource_kind"], `${path}.line.resource_kind`),
        used: readWholeNumber(one["used"], `${path}.line.used`),
        limitAfter: readWholeNumber(one["limit_after"], `${path}.line.limit_after`),
        overBy: readWholeNumber(one["over_by"], `${path}.line.over_by`),
        restriction: restrictionOf(one["restriction"], `${path}.line.restriction`),
        affected: readArray(one["affected"], `${path}.line.affected`).map((row, at) => {
          const each = readRecord(row, `${path}.line.affected[${at}]`);
          return { id: readText(each["id"], `${path}.line.affected.id`), name: readText(each["name"], `${path}.line.affected.name`) };
        }),
      };
    }),
  };
}

export function subscriptionApiSlice(client: WeTalkApiClient, _context: SliceContext): SubscriptionGatewaySlice {
  return {
    async readBalanceSubscription(billingAccountId) {
      const data = dataOf(await client.getBalanceSubscription({ path: { billing_account_id: billingAccountId } }), "getBalanceSubscription");
      return balanceSubscriptionOfWire(data, "getBalanceSubscription.data");
    },
    async quoteSubscription(accountId, request) {
      const body = assertSubscriptionOk(await client.createBillingQuote({ path: { account_id: accountId }, body: subscriptionQuoteRequestBodyOf(request) }));
      const data = readRecord(readRecord(body, "createBillingQuote")["data"], "createBillingQuote.data");
      return subscriptionQuoteOfWire(data, "createBillingQuote.data");
    },
    async withdrawSubscriptionCancellation(billingSubscriptionId) {
      const body = assertSubscriptionOk(await client.withdrawSubscriptionCancellation({ path: { billing_subscription_id: billingSubscriptionId } }));
      const data = readRecord(readRecord(body, "withdrawSubscriptionCancellation")["data"], "withdrawSubscriptionCancellation.data");
      return subscriptionOfWire(data, "withdrawSubscriptionCancellation.data");
    },
    async listResourceRestrictions(accountId) {
      const operation = documentedOperation(client, PLAN_OPERATION.restriction);
      const body = readRecord(assertOk(await operation({ path: { account_id: accountId } })), "listResourceRestrictions");
      return readArray(body["data"], "listResourceRestrictions.data").map((raw, index) =>
        resourceRestrictionOfWire(readRecord(raw, `listResourceRestrictions.data[${String(index)}]`), `listResourceRestrictions.data[${String(index)}]`),
      );
    },
    async readEntitlementImpact(billingAccountId, commercialRevisionId) {
      const operation = documentedOperation(client, PLAN_OPERATION.impact);
      const data = dataOf(await operation({ path: { billing_account_id: billingAccountId }, query: { commercial_revision_id: commercialRevisionId } }), "getEntitlementImpact");
      return entitlementImpactOfWire(data, "getEntitlementImpact.data");
    },
  };
}
